// src/pages/subscription/components/BusinessDetails.jsx
import React, { useState, useEffect } from 'react';
import { Building2, Globe, Clock, DollarSign, Calendar, Edit2, Save, X, Upload, MapPin, Phone } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { useAuth } from '../../../hooks/useAuth';
import { formatDateTime } from '../../../utils/dateUtils';
import { getMySubscription } from '../../../services/api/subscription';
import { getBusinessProfile, updateBusinessProfile } from '../../../services/api/business';
import { getImageUrl } from '../../../utils/imageUtils';
import styles from './BusinessDetails.module.css';

const BusinessDetails = ({ business }) => {
  const { t } = useLocalization();
  const { error: showError, success } = useAlert();
  const { getUserPermissions, refreshUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [editData, setEditData] = useState({
    businessName: '',
    legalName: '',
    businessAddress: '',
    businessPhone: '',
    website: '',
    description: '',
    logo: null
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [originalLogo, setOriginalLogo] = useState(null);

  // Check if user has permission to edit
  const canEdit = getUserPermissions().includes('view_subscription');

  useEffect(() => {
    loadSubscription();
    loadBusinessProfile();
  }, []);

  useEffect(() => {
    if (business) {
      setEditData({
        businessName: business.name || '',
        legalName: business.legalName || '',
        businessAddress: business.businessAddress || '',
        businessPhone: business.businessPhone || '',
        website: business.website || '',
        description: business.description || '',
        logo: null
      });

      if (business.logo) {
        const logoUrl = getImageUrl(business.logo);
        setLogoPreview(logoUrl);
        setOriginalLogo(logoUrl);
      }
    }
  }, [business]);

  const loadSubscription = async () => {
    try {
      const response = await getMySubscription();
      setSubscription(response.subscription);
    } catch (err) {
      console.error('Error loading subscription:', err);
      if (err.response?.status !== 404) {
        showError(t('subscription.loadSubscriptionFailed'));
      }
    }
  };

  const loadBusinessProfile = async () => {
    try {
      const response = await getBusinessProfile();
      const businessData = response.data;
      
      setEditData({
        businessName: businessData.name || '',
        legalName: businessData.legalName || '',
        businessAddress: businessData.businessAddress || '',
        businessPhone: businessData.businessPhone || '',
        website: businessData.website || '',
        description: businessData.description || '',
        logo: null
      });

      if (businessData.logo) {
        const logoUrl = getImageUrl(businessData.logo);
        setLogoPreview(logoUrl);
        setOriginalLogo(logoUrl);
      }
    } catch (err) {
      console.error('Error loading business profile:', err);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Business Name (required)
    if (!editData.businessName.trim()) {
      newErrors.businessName = t('business.businessNameRequired');
    } else if (editData.businessName.trim().length < 2) {
      newErrors.businessName = t('business.businessNameMinLength');
    }

    // Legal Name (required)
    if (!editData.legalName.trim()) {
      newErrors.legalName = t('business.legalNameRequired');
    } else if (editData.legalName.trim().length < 2) {
      newErrors.legalName = t('business.legalNameMinLength');
    }

    // Phone validation (optional)
    if (editData.businessPhone && !/^[0-9+\-\s()]+$/.test(editData.businessPhone)) {
      newErrors.businessPhone = t('business.invalidPhone');
    }

    // Website validation (optional)
    if (editData.website) {
      try {
        new URL(editData.website);
      } catch {
        newErrors.website = t('business.invalidWebsite');
      }
    }

    // Logo validation (optional)
    if (editData.logo) {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(editData.logo.type)) {
        newErrors.logo = t('business.invalidLogoFormat');
      } else if (editData.logo.size > 2 * 1024 * 1024) {
        newErrors.logo = t('business.logoTooLarge');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate immediately
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          logo: t('business.invalidLogoFormat')
        }));
        return;
      }
      
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          logo: t('business.logoTooLarge')
        }));
        return;
      }

      setEditData(prev => ({
        ...prev,
        logo: file
      }));

      // Create preview immediately
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);

      if (errors.logo) {
        setErrors(prev => ({
          ...prev,
          logo: ''
        }));
      }
    }
  };

  const handleRemoveLogo = () => {
    setEditData(prev => ({
      ...prev,
      logo: null
    }));
    
    // Reset to original logo if it exists
    if (originalLogo) {
      setLogoPreview(originalLogo);
    } else {
      setLogoPreview(null);
    }

    // Reset file input
    const fileInput = document.getElementById('logoInput');
    if (fileInput) fileInput.value = '';
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    // Reset to original values
    setEditData({
      businessName: business.name || '',
      legalName: business.legalName || '',
      businessAddress: business.businessAddress || '',
      businessPhone: business.businessPhone || '',
      website: business.website || '',
      description: business.description || '',
      logo: null
    });

    // Reset logo preview
    if (originalLogo) {
      setLogoPreview(originalLogo);
    } else {
      setLogoPreview(null);
    }

    setErrors({});
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsUpdating(true);

    try {
      const submitData = new FormData();
      submitData.append('businessName', editData.businessName.trim());
      submitData.append('legalName', editData.legalName.trim());
      
      if (editData.businessAddress.trim()) {
        submitData.append('businessAddress', editData.businessAddress.trim());
      }
      
      if (editData.businessPhone.trim()) {
        submitData.append('businessPhone', editData.businessPhone.trim());
      }
      
      if (editData.website.trim()) {
        submitData.append('website', editData.website.trim());
      }
      
      if (editData.description.trim()) {
        submitData.append('description', editData.description.trim());
      }

      // Only append logo if a new file was selected
      if (editData.logo) {
        submitData.append('logo', editData.logo);
      }

      const response = await updateBusinessProfile(submitData);
      
      // Update original logo reference if logo was changed
      if (response.data.logo) {
        const newLogoUrl = getImageUrl(response.data.logo);
        setOriginalLogo(newLogoUrl);
      }

      success(t('business.businessUpdatedSuccess'));
      setIsEditing(false);
      
      // Refresh user data to update business info in context
      await refreshUser();
    } catch (err) {
      console.error('Business update failed:', err);
      if (err.response?.data?.message) {
        showError(err.response.data.message);
      } else {
        showError(t('business.businessUpdateError'));
      }
    } finally {
      setIsUpdating(false);
    }
  };

  if (!business) {
    return null;
  }

  // Read-only details (non-editable)
  const readOnlyDetails = [
    {
      icon: DollarSign,
      label: t('subscription.currency'),
      value: business.currency || 'LKR'
    },
    {
      icon: Clock,
      label: t('subscription.timezone'),
      value: business.timezone || 'Asia/Colombo'
    },
    {
      icon: Globe,
      label: t('common.country'),
      value: business.country || 'N/A'
    }
  ];

  // Add subscription details if available
  if (subscription) {
    readOnlyDetails.push({
      icon: Calendar,
      label: t('subscription.memberSince'),
      value: formatDateTime(subscription.Business.createdAt)
    });
  }

  return (
    <div className={styles.businessDetails}>
      <div className={styles.header}>
        <h2>{t('subscription.businessDetails')}</h2>
        {canEdit && (
          <div className={styles.headerActions}>
            {!isEditing ? (
              <button 
                className={styles.editButton}
                onClick={handleEdit}
              >
                <Edit2 size={16} />
                {t('business.edit')}
              </button>
            ) : (
              <div className={styles.editActions}>
                <button 
                  className={styles.cancelButton}
                  onClick={handleCancel}
                  disabled={isUpdating}
                >
                  <X size={16} />
                  {t('business.cancel')}
                </button>
                <button 
                  className={styles.saveButton}
                  onClick={handleSave}
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <div className={styles.spinner}></div>
                      {t('common.saving')}
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      {t('business.save')}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.detailsContent}>
        {/* Editable Section */}
        <div className={styles.editableSection}>
          {isEditing ? (
            <div className={styles.editForm}>
              {/* Logo Upload */}
              <div className={styles.logoSection}>
                <label className={styles.logoLabel}>{t('business.logo')}</label>
                <div className={styles.logoUploadArea}>
                  {logoPreview ? (
                    <div className={styles.logoPreview}>
                      <img src={logoPreview} alt="Business logo" className={styles.logoImage} />
                      <div className={styles.logoOverlay}>
                        <button
                          type="button"
                          className={styles.changeLogoButton}
                          onClick={() => document.getElementById('logoInput').click()}
                        >
                          <Upload size={16} />
                          {t('business.changeLogo')}
                        </button>
                        <button
                          type="button"
                          className={styles.removeLogoButton}
                          onClick={handleRemoveLogo}
                        >
                          <X size={16} />
                          {t('business.removeLogo')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className={styles.logoUploadPlaceholder}
                      onClick={() => document.getElementById('logoInput').click()}
                    >
                      <Upload size={32} className={styles.uploadIcon} />
                      <p>{t('business.uploadLogo')}</p>
                      <span className={styles.logoHint}>{t('business.logoFormats')}</span>
                    </div>
                  )}
                  <input
                    id="logoInput"
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp"
                    onChange={handleLogoChange}
                    style={{ display: 'none' }}
                  />
                  {errors.logo && <span className={styles.errorText}>{errors.logo}</span>}
                </div>
              </div>

              {/* Business Name */}
              <div className={styles.formGroup}>
                <label htmlFor="businessName">
                  {t('business.businessName')} *
                </label>
                <input
                  type="text"
                  id="businessName"
                  name="businessName"
                  value={editData.businessName}
                  onChange={handleInputChange}
                  placeholder={t('business.businessNamePlaceholder')}
                  className={errors.businessName ? styles.error : ''}
                />
                {errors.businessName && (
                  <span className={styles.errorText}>{errors.businessName}</span>
                )}
              </div>

              {/* Legal Name */}
              <div className={styles.formGroup}>
                <label htmlFor="legalName">
                  {t('business.legalName')} *
                </label>
                <input
                  type="text"
                  id="legalName"
                  name="legalName"
                  value={editData.legalName}
                  onChange={handleInputChange}
                  placeholder={t('business.legalNamePlaceholder')}
                  className={errors.legalName ? styles.error : ''}
                />
                {errors.legalName && (
                  <span className={styles.errorText}>{errors.legalName}</span>
                )}
                <span className={styles.hint}>{t('business.legalNameHint')}</span>
              </div>

              {/* Business Address */}
              <div className={styles.formGroup}>
                <label htmlFor="businessAddress">
                  {t('business.businessAddress')}
                </label>
                <div className={styles.inputWithIcon}>
                  <MapPin className={styles.inputIcon} size={18} />
                  <input
                    type="text"
                    id="businessAddress"
                    name="businessAddress"
                    value={editData.businessAddress}
                    onChange={handleInputChange}
                    placeholder={t('business.businessAddressPlaceholder')}
                  />
                </div>
              </div>

              {/* Phone and Website Row */}
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="businessPhone">
                    {t('business.businessPhone')}
                  </label>
                  <div className={styles.inputWithIcon}>
                    <Phone className={styles.inputIcon} size={18} />
                    <input
                      type="tel"
                      id="businessPhone"
                      name="businessPhone"
                      value={editData.businessPhone}
                      onChange={handleInputChange}
                      placeholder={t('business.businessPhonePlaceholder')}
                      className={errors.businessPhone ? styles.error : ''}
                    />
                  </div>
                  {errors.businessPhone && (
                    <span className={styles.errorText}>{errors.businessPhone}</span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="website">
                    {t('business.website')}
                  </label>
                  <div className={styles.inputWithIcon}>
                    <Globe className={styles.inputIcon} size={18} />
                    <input
                      type="url"
                      id="website"
                      name="website"
                      value={editData.website}
                      onChange={handleInputChange}
                      placeholder={t('business.websitePlaceholder')}
                      className={errors.website ? styles.error : ''}
                    />
                  </div>
                  {errors.website && (
                    <span className={styles.errorText}>{errors.website}</span>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className={styles.formGroup}>
                <label htmlFor="description">
                  {t('business.description')}
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={editData.description}
                  onChange={handleInputChange}
                  placeholder={t('business.descriptionPlaceholder')}
                  rows="3"
                  className={styles.textarea}
                />
              </div>
            </div>
          ) : (
            <div className={styles.detailsGrid}>
              {/* Logo Display */}
              {logoPreview && (
                <div className={`${styles.detailItem} ${styles.logoItem}`}>
                  <div className={styles.logoDisplay}>
                    <img src={logoPreview} alt="Business logo" className={styles.logoImageView} />
                  </div>
                  <div className={styles.detailContent}>
                    <div className={styles.detailLabel}>{t('business.logo')}</div>
                  </div>
                </div>
              )}

              {/* Business Name */}
              <div className={`${styles.detailItem} ${styles.primary}`}>
                <div className={styles.iconWrapper}>
                  <Building2 size={20} />
                </div>
                <div className={styles.detailContent}>
                  <div className={styles.detailLabel}>{t('business.businessName')}</div>
                  <div className={styles.detailValue}>{editData.businessName}</div>
                </div>
              </div>

              {/* Legal Name */}
              <div className={styles.detailItem}>
                <div className={styles.iconWrapper}>
                  <Building2 size={20} />
                </div>
                <div className={styles.detailContent}>
                  <div className={styles.detailLabel}>{t('business.legalName')}</div>
                  <div className={styles.detailValue}>{editData.legalName || 'N/A'}</div>
                </div>
              </div>

              {/* Business Address */}
              {editData.businessAddress && (
                <div className={styles.detailItem}>
                  <div className={styles.iconWrapper}>
                    <MapPin size={20} />
                  </div>
                  <div className={styles.detailContent}>
                    <div className={styles.detailLabel}>{t('business.businessAddress')}</div>
                    <div className={styles.detailValue}>{editData.businessAddress}</div>
                  </div>
                </div>
              )}

              {/* Business Phone */}
              {editData.businessPhone && (
                <div className={styles.detailItem}>
                  <div className={styles.iconWrapper}>
                    <Phone size={20} />
                  </div>
                  <div className={styles.detailContent}>
                    <div className={styles.detailLabel}>{t('business.businessPhone')}</div>
                    <div className={styles.detailValue}>{editData.businessPhone}</div>
                  </div>
                </div>
              )}

              {/* Website */}
              {editData.website && (
                <div className={styles.detailItem}>
                  <div className={styles.iconWrapper}>
                    <Globe size={20} />
                  </div>
                  <div className={styles.detailContent}>
                    <div className={styles.detailLabel}>{t('business.website')}</div>
                    <div className={styles.detailValue}>
                      <a 
                        href={editData.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={styles.websiteLink}
                      >
                        {editData.website}
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Description */}
              {editData.description && (
                <div className={`${styles.detailItem} ${styles.fullWidth}`}>
                  <div className={styles.iconWrapper}>
                    <Building2 size={20} />
                  </div>
                  <div className={styles.detailContent}>
                    <div className={styles.detailLabel}>{t('business.description')}</div>
                    <div className={styles.detailValue}>{editData.description}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Read-only Section */}
        <div className={styles.readOnlySection}>
          <div className={styles.detailsGrid}>
            {readOnlyDetails.map((detail, index) => {
              const Icon = detail.icon;
              return (
                <div key={index} className={styles.detailItem}>
                  <div className={styles.iconWrapper}>
                    <Icon size={20} />
                  </div>
                  <div className={styles.detailContent}>
                    <div className={styles.detailLabel}>{detail.label}</div>
                    <div className={styles.detailValue}>{detail.value}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessDetails;